/**
 * Onboarding Walkthrough Service
 * Manages the state and logic for the onboarding walkthrough feature
 */

// Step definitions for the walkthrough
export const WALKTHROUGH_STEPS = [
  {
    id: 'welcome',
    target: 'page-center',
    content: {
      title: '👋 Welcome to Burnt',
      description: "Let's take a quick tour to help you get started!",
      actionText: "Let's get started"
    },
    placement: 'center',
    trigger: 'acknowledge',
    pageUrl: '/network',
    isWelcome: true
  },
  {
    id: 'members-directory-overview',
    target: 'navigation-tab',
    highlightTab: 'Members',
    content: {
      title: 'Meet the Members',
      description: 'This is your directory to all members in the Burnt community. Use the search and filter tools to find and connect with peers, mentors, and future colleagues.',
      actionText: 'Next: Explore Jobs'
    },
    placement: 'adjacent',
    trigger: 'acknowledge',
    pageUrl: '/network'
  },
  {
    id: 'jobs-overview',
    target: 'navigation-tab',
    highlightTab: 'Jobs',
    content: {
      title: 'Jobs',
      description: 'Looking for your next opportunity? Our job board features curated roles from our trusted partners who are actively hiring.',
      actionText: 'Next: See what members are talking about'
    },
    placement: 'adjacent',
    trigger: 'acknowledge',
    pageUrl: '/jobs'
  },
  {
    id: 'forum-overview',
    target: 'navigation-tab',
    highlightTab: 'Forum',
    content: {
      title: 'Welcome to the Forum',
      description: 'Check out what members are discussing, from industry trends to in-person meetups. It\'s the heart of our community.'
    },
    placement: 'adjacent',
    trigger: 'acknowledge',
    pageUrl: '/forum'
  },
  {
    id: 'forum-participation-encouragement',
    target: 'post-button',
    highlightElement: '[data-walkthrough-element="post-button"]',
    content: {
      title: 'Make Your First Post',
      description: 'Ready to join in? The best way to start is by introducing yourself. Share who you are, what you\'re working on, and what you\'re looking for.',
      actionText: 'Get started'
    },
    placement: 'adjacent',
    trigger: 'acknowledge',
    pageUrl: '/forum'
  }
];

const STORAGE_KEY = 'burnt-walkthrough-progress';
const STORAGE_VERSION = '2.0'; // Bumped to reset cache after step removal

class OnboardingWalkthroughService {
  constructor() {
    this.state = {
      isActive: false,
      currentStepIndex: 0,
      isCompleted: false,
      isSkipped: false,
      steps: WALKTHROUGH_STEPS,
      userId: null,
      startedAt: null,
      completedAt: null
    };
    this.listeners = [];
    this.firestore = null;
  }

  /**
   * Initialize the walkthrough service
   */
  initialize(userId, userProfile, firestore) {
    this.state.userId = userId;
    this.firestore = firestore;

    // Check if walkthrough is already completed
    const isCompleted = this.isWalkthroughCompleted(userProfile);
    
    if (isCompleted) {
      this.state.isCompleted = true;
      return false; // Don't start walkthrough
    }

    return true; // Ready to start
  }

  /**
   * Start the walkthrough
   */
  startWalkthrough() {
    this.state.isActive = true;
    this.state.currentStepIndex = 0;
    this.state.startedAt = new Date().toISOString();
    this.saveProgress();
    this.notifyListeners();
  }

  /**
   * Move to the next step
   */
  nextStep() {
    if (this.state.currentStepIndex < this.state.steps.length - 1) {
      this.state.currentStepIndex++;
      this.saveProgress();
      this.notifyListeners();
      return true;
    }
    return false;
  }

  /**
   * Move to the previous step
   */
  previousStep() {
    if (this.state.currentStepIndex > 0) {
      this.state.currentStepIndex--;
      this.saveProgress();
      this.notifyListeners();
      return true;
    }
    return false;
  }

  /**
   * Go to a specific step
   */
  goToStep(stepIndex) {
    if (stepIndex >= 0 && stepIndex < this.state.steps.length) {
      this.state.currentStepIndex = stepIndex;
      this.saveProgress();
      this.notifyListeners();
      return true;
    }
    return false;
  }

  /**
   * Complete the walkthrough
   */
  completeWalkthrough() {
    this.state.isActive = false;
    this.state.isCompleted = true;
    this.state.completedAt = new Date().toISOString();
    this.saveProgress();
    this.saveToFirestore();
    this.notifyListeners();
  }

  /**
   * Skip the walkthrough
   */
  skipWalkthrough() {
    this.state.isActive = false;
    this.state.isSkipped = true;
    this.state.isCompleted = true;
    this.state.completedAt = new Date().toISOString();
    this.saveProgress();
    this.saveToFirestore();
    this.notifyListeners();
  }

  /**
   * Reset the walkthrough (for testing)
   */
  resetWalkthrough() {
    this.state = {
      isActive: false,
      currentStepIndex: 0,
      isCompleted: false,
      isSkipped: false,
      steps: WALKTHROUGH_STEPS,
      userId: this.state.userId,
      startedAt: null,
      completedAt: null
    };
    this.clearProgress();
    this.clearFirestoreProgress();
    this.notifyListeners();
  }

  /**
   * Check if walkthrough is completed
   */
  isWalkthroughCompleted(userProfile) {
    // Check Firestore first (with version validation)
    if (userProfile?.walkthrough?.isCompleted && 
        userProfile?.walkthrough?.version === STORAGE_VERSION) {
      return true;
    }

    // Check localStorage as fallback (loadProgress already validates version)
    const progress = this.loadProgress();
    return progress?.isCompleted || false;
  }

  /**
   * Get current step
   */
  getCurrentStep() {
    return this.state.steps[this.state.currentStepIndex];
  }

  /**
   * Get current step index
   */
  getCurrentStepIndex() {
    return this.state.currentStepIndex;
  }

  /**
   * Get total steps
   */
  getTotalSteps() {
    return this.state.steps.length;
  }

  /**
   * Get current state
   */
  getState() {
    return { ...this.state };
  }

  /**
   * Save progress to localStorage
   */
  saveProgress() {
    try {
      const progressData = {
        userId: this.state.userId,
        isCompleted: this.state.isCompleted,
        isSkipped: this.state.isSkipped,
        currentStep: this.state.currentStepIndex,
        completedAt: this.state.completedAt,
        skippedAt: this.state.isSkipped ? this.state.completedAt : null,
        version: STORAGE_VERSION
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
    } catch (error) {
      console.warn('Failed to save walkthrough progress to localStorage:', error);
    }
  }

  /**
   * Load progress from localStorage
   */
  loadProgress() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return null;
      
      const parsed = JSON.parse(data);
      
      // Invalidate old versions
      if (parsed.version !== STORAGE_VERSION) {
        console.log('Clearing outdated walkthrough progress');
        this.clearProgress();
        return null;
      }
      
      return parsed;
    } catch (error) {
      console.warn('Failed to load walkthrough progress from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear progress from localStorage
   */
  clearProgress() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear walkthrough progress from localStorage:', error);
    }
  }

  /**
   * Save progress to Firestore
   */
  async saveToFirestore() {
    if (!this.firestore || !this.state.userId) {
      return;
    }

    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const userRef = doc(this.firestore, 'user_profiles', this.state.userId);
      
      await updateDoc(userRef, {
        'walkthrough.isCompleted': this.state.isCompleted,
        'walkthrough.isSkipped': this.state.isSkipped,
        'walkthrough.completedAt': this.state.completedAt,
        'walkthrough.skippedAt': this.state.isSkipped ? this.state.completedAt : null,
        'walkthrough.version': STORAGE_VERSION
      });
    } catch (error) {
      console.warn('Failed to save walkthrough progress to Firestore:', error);
    }
  }

  /**
   * Clear progress from Firestore
   */
  async clearFirestoreProgress() {
    if (!this.firestore || !this.state.userId) {
      return;
    }

    try {
      const { doc, updateDoc, deleteField } = await import('firebase/firestore');
      const userRef = doc(this.firestore, 'user_profiles', this.state.userId);
      
      await updateDoc(userRef, {
        walkthrough: deleteField()
      });
    } catch (error) {
      console.warn('Failed to clear walkthrough progress from Firestore:', error);
    }
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of state changes
   */
  notifyListeners() {
    this.listeners.forEach(listener => listener(this.getState()));
  }

  /**
   * Cleanup
   */
  cleanup() {
    this.listeners = [];
  }
}

// Create singleton instance
const walkthroughService = new OnboardingWalkthroughService();

export default walkthroughService;
